/**
 * Composable per Cross Company Target Job Orders autocomplete con loading e error handling centralizzati
 */

import { ref, readonly } from 'vue';
import { useODataBuilder } from '../useODataBuilder';
import { useLoadingState } from '../useLoadingState';
import { useNotification } from '../useNotification';
import { useStaleSafeRequest } from '../useStaleSafeRequest';
import type { BaseAutocompleteComposableOptions } from './types';
import type { CrossCompanyTargetJobOrdersOptions, AutocompleteResult } from '@/lib/autocompletes';
import { getCrossCompanyTargetJobOrdersAutocomplete } from '@/lib/autocompletes/crossCompanyTargetJobOrders';
// TODO: Usare CrossCompanyTargetJobOrderAutocompleteResult quando sarà creato

export function useCrossCompanyTargetJobOrdersAutocomplete(
	options: BaseAutocompleteComposableOptions = {},
) {
	const targetJobOrders = ref<AutocompleteResult[]>([]);

	const { buildOdataQuery } = useODataBuilder();
	const withLoadingFn = options.withLoading || useLoadingState().withLoading;
	const { error } = useNotification();
	const { requestToken } = useStaleSafeRequest();

	async function searchCrossCompanyTargetJobOrders(
		options: CrossCompanyTargetJobOrdersOptions,
	): Promise<void> {
		const token = requestToken();
		await withLoadingFn(async () => {
			try {
				const result = await getCrossCompanyTargetJobOrdersAutocomplete(buildOdataQuery, options);
				if (token.isLatest()) {
					targetJobOrders.value = result;
				}
			} catch (err) {
				console.error(
					'❌ ERROR useCrossCompanyTargetJobOrdersAutocomplete.searchCrossCompanyTargetJobOrders:',
					err,
				);
				error(
					'Errore durante il caricamento delle commesse cross-company! Ricaricare la pagina e riprovare.',
				);
				if (token.isLatest()) {
					targetJobOrders.value = [];
				}
			}
		}, 'searchCrossCompanyTargetJobOrders');
	}

	function clearTargetJobOrders(): void {
		targetJobOrders.value = [];
	}

	return {
		targetJobOrders: readonly(targetJobOrders),
		searchCrossCompanyTargetJobOrders,
		clearTargetJobOrders,
	};
}
